'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import * as CustomPropTypes from '../../../constants/PropTypes';
import styled from 'styled-components';
import { GYPSUM } from 'HubStyleTokens/colors';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { useWindowDimensionsContext } from 'ecosystem-marketplace-core/hooks/useWindowDimensionsContext';
import { usePrevious } from 'ecosystem-marketplace-core/hooks/usePrevious';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import PricingPlanCard from './PricingPlanCard';
const ScrollButton = styled(UIIconButton).withConfig({
  displayName: "Pricings__ScrollButton",
  componentId: "sc-1q4bqa4-0"
})(["position:absolute;top:45%;", ":-40px;"], props => props.direction);
const CARD_X_MARGIN = 12;
const CARD_MARGIN_SCROLL_BUFFER_FACTOR = 4.5;

const Pricings = ({
  currency,
  onTrackInteraction,
  pricings
}) => {
  const scrollRef = useRef(null);
  const [currentVisibleCardIndex, setCurrentVisibleCardIndex] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const {
    width: windowWidth
  } = useWindowDimensionsContext();
  const prevWindowWidth = usePrevious(windowWidth);
  useEffect(() => {
    if (scrollRef.current && windowWidth !== prevWindowWidth) {
      setIsOverflow(scrollRef.current.scrollWidth > scrollRef.current.offsetWidth);
    }
  }, [prevWindowWidth, windowWidth]);
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const isScrolledToBeginning = useMemo(() => {
    return currentVisibleCardIndex === 0;
  }, [currentVisibleCardIndex]);
  const isScrolledToEnd = useMemo(() => {
    return currentVisibleCardIndex >= pricings.size - 1;
  }, [currentVisibleCardIndex, pricings]);
  const scrollToCard = useCallback(index => {
    if (index >= 0 && index < pricings.size) {
      const cardRef = document.querySelector(`div[data-pricing-index="${index}"]`);

      if (cardRef && scrollRef.current) {
        setCurrentVisibleCardIndex(index);
        scrollRef.current.scrollBy(cardRef && cardRef.getBoundingClientRect().x - CARD_X_MARGIN * CARD_MARGIN_SCROLL_BUFFER_FACTOR, 0);
      }
    }
  }, [pricings]);
  const scrollToNext = useCallback(() => {
    const index = currentVisibleCardIndex + 1;

    if (isMobileView) {
      scrollToCard(index);
    } else if (scrollRef.current) {
      scrollRef.current.scrollTo(scrollRef.current.scrollWidth - scrollRef.current.offsetWidth, 0);
    }
  }, [scrollToCard, currentVisibleCardIndex, isMobileView]);
  const scrollToPrevious = useCallback(() => {
    const index = currentVisibleCardIndex - 1;

    if (isMobileView) {
      scrollToCard(index);
    } else if (scrollRef.current) {
      scrollRef.current.scrollTo(0, 0);
    }
  }, [scrollToCard, currentVisibleCardIndex, isMobileView]);
  const pricingCards = useMemo(() => {
    return pricings.sortBy((plan, index) => plan.get('planType') === PlanType.FREE ? -1 : index).map((plan, index) => /*#__PURE__*/_jsx(PricingPlanCard, {
      className: index === 0 ? `m-right-${CARD_X_MARGIN / 4}` : `m-x-${CARD_X_MARGIN / 4}`,
      currency: currency,
      onTrackInteraction: onTrackInteraction,
      plan: plan,
      cardIndex: index,
      setVisibleCardIndex: setCurrentVisibleCardIndex
    }, `plan-${index}`));
  }, [currency, onTrackInteraction, pricings]);
  return /*#__PURE__*/_jsxs("div", {
    style: {
      position: 'relative'
    },
    children: [/*#__PURE__*/_jsx(UIScrollContainer, {
      gradientColor: GYPSUM,
      scrollElementRef: scrollRef,
      children: /*#__PURE__*/_jsx(UIFlex, {
        align: "stretch",
        children: pricingCards
      })
    }), isOverflow && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(ScrollButton, {
        direction: "left",
        size: "sm",
        disabled: isScrolledToBeginning,
        onClick: scrollToPrevious,
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "left"
        })
      }), /*#__PURE__*/_jsx(ScrollButton, {
        direction: "right",
        size: "sm",
        disabled: isScrolledToEnd,
        onClick: scrollToNext,
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "right"
        })
      })]
    })]
  });
};

Pricings.propTypes = {
  currency: PropTypes.string.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  pricings: ImmutablePropTypes.listOf(CustomPropTypes.pricingPlan).isRequired
};
export default Pricings;